/**
 * Utility functions for date and time
 */
import moment from 'moment';
import { isBlank } from 'util/GeneralUtils';

/**
 * Convert timestamp to date string.
 * By default, use local date time, and ISO date format.
 * Null timestamp will be converted to empty string.
 */
export function timestampToDateString({
  timestamp,
  isUTC = false,
  format = 'YYYY-MM-DD'
}: {
  timestamp: number | null;
  isUTC?: boolean;
  format?: string;
}): string {
  if (timestamp === null) return '';
  const theMoment = isUTC ? moment(timestamp).utc() : moment(timestamp);
  return theMoment.format(format);
}

/**
 * Convert date string to timestamp.
 * By default, use local date time, and convert to the start of the day.
 * Empty string will be converted to undefined timestamp.
 */
export function dateStringToTimestamp({
  dateString,
  isUTC = false,
  isStartOfDay = true
}: {
  dateString: string;
  isUTC?: boolean;
  isStartOfDay?: boolean;
}): number | undefined {
  if (dateString.trim() === '') return undefined;
  let theMoment = moment(dateString);
  if (isUTC) theMoment = theMoment.utc();
  theMoment = isStartOfDay ? theMoment.startOf('day') : theMoment.endOf('day');
  return theMoment.valueOf();
}

/**
 * Convert date and time string to timestamp.
 * By default, use local date time
 * Empty date string or time string will be treated as invalid data and 
 * converted to undefined timestamp.
 */
export function dateTimeStringToTimestamp({
  dateString,
  timeString,
  isUTC = false
}: {
  dateString: string;
  timeString: string;
  isUTC?: boolean;
}): number | undefined {
  if (isBlank(dateString) || isBlank(timeString)) {
    return undefined;
  }
  const dateTimeString = `${dateString}T${timeString}${isUTC ? 'Z' : ''}`;
  return Date.parse(dateTimeString);
}
